import { useEffect, useId, useRef, useState, type KeyboardEvent } from 'react';

import { cx } from '../../lib/cx';
import { Icon, type IconName } from '../Icon';

import styles from './Select.module.css';

export interface SelectOption<Value extends string> {
  value: Value;
  label: string;
  /** Language of the label, when it is not the page's: a screen reader then reads it right. */
  lang?: string;
  /** What a `compact` control shows of it when chosen ("RU" for "Русский"); the list shows `label`. */
  short?: string;
}

export interface SelectProps<Value extends string> {
  /** What is being chosen: the control's name for assistive technology. */
  label: string;
  options: readonly SelectOption<Value>[];
  value: Value;
  onChange: (value: Value) => void;
  /** Shown before the chosen value. */
  icon?: IconName;
  /** sm: 36 px, for bars and menus; md follows the device, like a field. */
  size?: 'sm' | 'md';
  /** Which edge of the control the list lines up with. */
  align?: 'start' | 'end';
  /** Take the whole width of the container. */
  fullWidth?: boolean;
  /** Show the chosen option's `short` name and no chevron: for a bar with little room, such as a phone's. */
  compact?: boolean;
  disabled?: boolean;
  className?: string;
}

/**
 * One choice from a short list, in a list of the kit's own.
 *
 * To assistive technology it is a select-only combobox: the arrows move through the
 * list, Enter or Space choose, Escape closes it and keeps the old choice.
 */
export function Select<Value extends string>({
  label,
  options,
  value,
  onChange,
  icon,
  size = 'md',
  align = 'start',
  fullWidth = false,
  compact = false,
  disabled,
  className,
}: SelectProps<Value>) {
  const id = useId();
  const root = useRef<HTMLDivElement>(null);
  const chosen = Math.max(0, options.findIndex((option) => option.value === value));
  // The option the keyboard is on while the list is open; null: the list is closed.
  const [active, setActive] = useState<number | null>(null);
  const open = active !== null;

  useEffect(() => {
    if (!open) return;
    const onPointer = (event: PointerEvent) => {
      if (!root.current?.contains(event.target as Node)) setActive(null);
    };
    document.addEventListener('pointerdown', onPointer);
    return () => document.removeEventListener('pointerdown', onPointer);
  }, [open]);

  const choose = (index: number) => {
    const option = options[index];
    setActive(null);
    if (option && option.value !== value) onChange(option.value);
  };

  const onKeyDown = (event: KeyboardEvent<HTMLButtonElement>) => {
    const last = options.length - 1;
    if (!open) {
      if (['ArrowDown', 'ArrowUp', 'Enter', ' '].includes(event.key)) {
        event.preventDefault();
        setActive(chosen);
      }
      return;
    }
    switch (event.key) {
      case 'ArrowDown':
        setActive(Math.min(last, active + 1));
        break;
      case 'ArrowUp':
        setActive(Math.max(0, active - 1));
        break;
      case 'Home':
        setActive(0);
        break;
      case 'End':
        setActive(last);
        break;
      case 'Enter':
      case ' ':
        choose(active);
        break;
      case 'Escape':
        // Closes the list only: not the dialog or the menu the control may be in.
        event.stopPropagation();
        setActive(null);
        break;
      case 'Tab':
        setActive(null);
        return;
      default:
        return;
    }
    event.preventDefault();
  };

  const current = options[chosen];
  return (
    <div ref={root} className={cx(styles.select, fullWidth && styles.fullWidth, className)}>
      <button
        type="button"
        role="combobox"
        aria-label={label}
        aria-haspopup="listbox"
        aria-expanded={open}
        aria-controls={`${id}-list`}
        aria-activedescendant={open ? `${id}-${active}` : undefined}
        disabled={disabled}
        className={cx(styles.trigger, styles[size])}
        onClick={() => setActive(open ? null : chosen)}
        onKeyDown={onKeyDown}
      >
        {icon ? <Icon name={icon} size={16} className={styles.lead} /> : null}
        <span className={styles.value} lang={current?.lang}>
          {compact ? (current?.short ?? current?.label) : current?.label}
        </span>
        {compact ? null : <Icon name="chevron-down" size={16} className={cx(styles.chevron, open && styles.turned)} />}
      </button>
      {open ? (
        <ul id={`${id}-list`} role="listbox" aria-label={label} className={cx(styles.list, styles[align])}>
          {options.map((option, index) => (
            <li
              key={option.value}
              id={`${id}-${index}`}
              role="option"
              aria-selected={option.value === value}
              lang={option.lang}
              className={cx(styles.option, index === active && styles.active)}
              // Before the button loses focus, so that the list is still there to be clicked.
              onPointerDown={(event) => event.preventDefault()}
              onPointerEnter={() => setActive(index)}
              onClick={() => choose(index)}
            >
              <span className={styles.optionLabel}>{option.label}</span>
              {option.value === value ? <Icon name="check" size={16} className={styles.check} /> : null}
            </li>
          ))}
        </ul>
      ) : null}
    </div>
  );
}
